import { defineStore } from 'pinia'
import type { App } from '@/datasources/api/apps'
import type { Service } from '@/datasources/api/services'
import { fetchAppsApi, listServicesApi } from '@/datasources/api'

const ACTIVE_APP_KEY = 'si-active-app'

interface AppState {
  apps: App[]
  selectedAppId: string | null
  selectedStage: string | null
  loading: boolean
  servicesMap: Record<string, Service[]>
}

export const useAppStore = defineStore('app', {
  state: (): AppState => ({
    apps: [],
    selectedAppId: localStorage.getItem(ACTIVE_APP_KEY),
    selectedStage: null,
    loading: false,
    servicesMap: {},
  }),
  getters: {
    currentApp: (state) => state.apps.find((a) => a.id === state.selectedAppId) ?? null,
    currentServices: (state) => {
      if (!state.selectedAppId) return []
      return state.servicesMap[state.selectedAppId] ?? []
    },
    stages: (state) => {
      if (!state.selectedAppId) return []
      const services = state.servicesMap[state.selectedAppId]
      if (!services) return []
      // Collect unique stages across all services in the app
      const entries = services.flatMap((svc) => svc.stages.map((s) => [s.name, s] as const))
      return Array.from(new Map(entries).values())
    },
  },
  actions: {
    async fetchApps() {
      this.loading = true
      try {
        const { data } = await fetchAppsApi()
        this.apps = data
        // A stale si-active-app (deleted app, other account, DB reset) must not
        // survive the fetch — fall back to the first app and re-persist.
        const selectedStillValid =
          this.selectedAppId != null && data.some((a) => a.id === this.selectedAppId)
        if (!selectedStillValid) {
          this.selectedAppId = data[0]?.id ?? null
        }
        if (this.selectedAppId) {
          localStorage.setItem(ACTIVE_APP_KEY, this.selectedAppId)
        } else {
          localStorage.removeItem(ACTIVE_APP_KEY)
        }
        if (!this.selectedStage && this.stages.length > 0) {
          this.selectedStage = ''
        }
      } catch {
        this.apps = []
      } finally {
        this.loading = false
      }
    },

    async fetchServices(appId: string) {
      this.loading = true
      try {
        const { data } = await listServicesApi(appId)
        if (!this.servicesMap) {
          this.servicesMap = {}
        }
        this.servicesMap[appId] = data
      } finally {
        this.loading = false
      }
    },
    selectApp(id: string) {
      this.selectedAppId = id
      this.selectedStage = null
      localStorage.setItem(ACTIVE_APP_KEY, id)
      // Ensure services for the newly selected app are loaded so
      // serviceCount / currentServices reflect real data on first visit.
      if (!this.servicesMap[id]) {
        void this.fetchServices(id)
      }
    },
    selectStage(stage: string) {
      this.selectedStage = stage
    },
  },
})
